import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Film, ImagePlus, Loader2 } from 'lucide-react';
import { fetchUrlImages, fetchUrlVideos } from '../../api/posts/fetch-post.api';
import {
  HOME_BANNER_IMAGE,
  isImageRatioFar,
  isVideoAspectOk,
  PRE_ROLL_VIDEO,
  PRE_ROLL_VIDEO_HINT,
} from '../../api/ads/ad-placements';

export interface CreativeFields {
  title?: string;
  body?: string;
  imageUrl?: string;
  videoUrl?: string;
  landingUrl?: string;
  ctaText?: string;
}

interface Props {
  value: CreativeFields;
  onChange: (patch: Partial<CreativeFields>) => void;
  /** Hiện ô link đích (form thay nội dung cần, form tạo chiến dịch đã có ô riêng). */
  showLanding?: boolean;
  disabled?: boolean;
  /** Chiến dịch có thể lên banner trang chủ: nhắc kích thước ảnh và cảnh báo (không chặn) khi tỉ lệ ảnh lệch nhiều. */
  homeBannerRatio?: boolean;
  /** Chiến dịch định dạng Video: cho tải video lên bằng đường tải video của bài viết, nhắc độ dài và khung hình. */
  allowVideo?: boolean;
}

/** Định dạng video nhận khi tải lên (khớp đường tải video của bài viết). */
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-m4v'];
export const isAcceptedVideoFile = (file: File) =>
  VIDEO_TYPES.includes(file.type) || (!file.type && /\.(mp4|webm|mov|m4v)$/i.test(file.name));

type VideoMeta = { url: string; duration: number; width: number; height: number };

/** Kích thước thật của ảnh theo đường dẫn (null khi chưa tải xong hoặc lỗi). */
const useImageSize = (url?: string) => {
  const [size, setSize] = useState<{ url: string; width: number; height: number } | null>(null);
  useEffect(() => {
    if (!url) return undefined;
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (alive) setSize({ url, width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = url;
    return () => {
      alive = false;
    };
  }, [url]);
  return size && size.url === url ? size : null;
};

const inputCls =
  'mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text';

/** Phần nhập nội dung quảng cáo dùng chung: tiêu đề, mô tả, ảnh, video đính kèm, nút kêu gọi. */
const AdCreativeForm: React.FC<Props> = ({ value, onChange, showLanding, disabled, homeBannerRatio, allowVideo }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoMeta, setVideoMeta] = useState<VideoMeta | null>(null);
  const lock = useRef(false);
  const meta = videoMeta && videoMeta.url === value.videoUrl ? videoMeta : null;
  const videoTooLong = !!meta && meta.duration > PRE_ROLL_VIDEO.warnOverSec;
  const videoAspectOff = !!meta && !isVideoAspectOk(meta.width, meta.height);

  const onUploadVideo = async (file?: File) => {
    if (!file || lock.current) return;
    if (!isAcceptedVideoFile(file)) {
      toast.error('Tệp này không phải video. Bạn hãy chọn một tệp video.');
      return;
    }
    lock.current = true;
    try {
      setUploadingVideo(true);
      const fd = new FormData();
      fd.append('type', file);
      // Đường tải video của bài viết: máy chủ trả từng tệp kèm trạng thái, tệp quá dung lượng có lý do rõ.
      const res = (await fetchUrlVideos(fd)) as Array<{ id?: string; url?: string; status?: string; error?: string }>;
      const ok = (res || []).find((v) => v?.status !== 'Failed' && v?.url);
      if (ok?.url) onChange({ videoUrl: ok.url });
      else toast.error(res?.find((v) => v?.error)?.error || 'Tải video thất bại.');
    } catch {
      toast.error('Tải video thất bại.');
    } finally {
      lock.current = false;
      setUploadingVideo(false);
    }
  };
  const imageSize = useImageSize(homeBannerRatio ? value.imageUrl : undefined);
  const ratioFar = !!imageSize && isImageRatioFar(imageSize.width, imageSize.height);

  const onUpload = async (file?: File) => {
    if (!file || lock.current) return;
    lock.current = true;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('type', file);
      const res = await fetchUrlImages(fd);
      if (res?.length) onChange({ imageUrl: res[0].url });
    } catch {
      toast.error('Tải ảnh thất bại.');
    } finally {
      lock.current = false;
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      {showLanding && (
        <label className="text-sm ui-text block">
          Link đích (khi bấm quảng cáo)
          <input
            className={inputCls}
            value={value.landingUrl || ''}
            onChange={(e) => onChange({ landingUrl: e.target.value })}
            placeholder="https://..."
            disabled={disabled}
          />
        </label>
      )}
      <label className="text-sm ui-text block">
        Tiêu đề quảng cáo
        <input
          className={inputCls}
          value={value.title || ''}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Tiêu đề hấp dẫn"
          disabled={disabled}
        />
      </label>
      <label className="text-sm ui-text block">
        Mô tả ngắn
        <textarea
          className={`${inputCls} resize-none`}
          rows={2}
          value={value.body || ''}
          onChange={(e) => onChange({ body: e.target.value })}
          disabled={disabled}
        />
      </label>
      <div className="flex items-center gap-3">
        <label className="inline-flex items-center gap-2 text-sm ui-text cursor-pointer rounded-md ui-divider border px-3 py-2">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
          Ảnh/banner
          <input
            type="file"
            accept="image/*"
            hidden
            disabled={disabled || uploading}
            onChange={(e) => {
              onUpload(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
        {value.imageUrl ? (
          <img src={value.imageUrl} alt="preview" className="h-12 rounded-md object-cover" />
        ) : null}
      </div>
      {homeBannerRatio ? (
        <p className="text-[11px] ui-muted -mt-1">
          Ảnh cho banner trang chủ nên là ảnh dọc {HOME_BANNER_IMAGE.width}×{HOME_BANNER_IMAGE.height} px.
        </p>
      ) : null}
      {homeBannerRatio && ratioFar && imageSize ? (
        <p role="alert" className="text-xs text-amber-600 dark:text-amber-400">
          Ảnh đang có kích thước {imageSize.width}×{imageSize.height} px, lệch nhiều so với khung banner trang chủ{' '}
          {HOME_BANNER_IMAGE.width}×{HOME_BANNER_IMAGE.height} px. Ảnh vẫn dùng được nhưng sẽ bị cắt bớt ở hai bên hoặc trên dưới.
        </p>
      ) : null}

      {allowVideo ? (
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm ui-text cursor-pointer rounded-md ui-divider border px-3 py-2">
            {uploadingVideo ? <Loader2 size={16} className="animate-spin" /> : <Film size={16} />}
            {value.videoUrl ? 'Đổi video' : 'Tải video lên'}
            <input
              type="file"
              accept={VIDEO_TYPES.join(',')}
              hidden
              data-testid="ad-video-input"
              disabled={disabled || uploadingVideo}
              onChange={(e) => {
                onUploadVideo(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
          <span className="text-[11px] ui-muted">{PRE_ROLL_VIDEO_HINT}, khung ngang 16:9 hoặc dọc 9:16.</span>
        </div>
      ) : null}

      {value.videoUrl ? (
        <div className="flex items-center gap-3 rounded-md ui-divider border p-2">
          <video
            src={value.videoUrl}
            className="h-16 rounded-md bg-black object-cover"
            muted
            preload="metadata"
            data-testid="ad-video-preview"
            onLoadedMetadata={(e) => {
              const v = e.currentTarget;
              setVideoMeta({ url: value.videoUrl || '', duration: v.duration, width: v.videoWidth, height: v.videoHeight });
            }}
          />
          <span className="text-xs ui-muted flex-1">
            {meta && Number.isFinite(meta.duration) ? `Video đã đính kèm, dài ${Math.round(meta.duration)} giây.` : 'Video đã đính kèm.'}
          </span>
          <button
            type="button"
            onClick={() => onChange({ videoUrl: '' })}
            className="text-xs text-rose-500 hover:underline"
          >
            Bỏ
          </button>
        </div>
      ) : null}
      {value.videoUrl && (videoTooLong || videoAspectOff) ? (
        <div role="alert" className="text-xs text-amber-600 dark:text-amber-400 space-y-0.5">
          {videoTooLong ? (
            <p>Video dài hơn {PRE_ROLL_VIDEO.warnOverSec} giây. {PRE_ROLL_VIDEO_HINT} để người xem không bỏ qua sớm.</p>
          ) : null}
          {videoAspectOff && meta ? (
            <p>
              Video có khung {meta.width}×{meta.height}, không gần 16:9 hoặc 9:16. Video vẫn dùng được nhưng sẽ có viền
              đen khi phát.
            </p>
          ) : null}
        </div>
      ) : null}

      <label className="text-sm ui-text block">
        Nút kêu gọi
        <input
          className={inputCls}
          value={value.ctaText || ''}
          onChange={(e) => onChange({ ctaText: e.target.value })}
          placeholder="VD: Tìm hiểu thêm"
          disabled={disabled}
        />
      </label>
    </div>
  );
};

export default AdCreativeForm;
